import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    LocationOn,
    Handshake,
    RocketLaunch,
    AdsClick,
    Apartment,
    ShoppingBag,
    Restaurant,
    Campaign,
    School,
    AccountBalance,
    MedicalServices,
    Gavel
} from '@mui/icons-material';

const IndustriesPage = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 60 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    return (
        <div className="font-sans text-gray-900 bg-white">
            {/* HERO */}
            <section className="bg-[hsl(264,50%,96%)] py-24 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-white/50 to-transparent pointer-events-none"></div>
                <div className="container mx-auto px-6 max-w-7xl text-center relative z-10">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6 bg-gradient-to-r from-gray-900 via-primary-700 to-[#F482C0] bg-clip-text text-transparent pb-2">
                            Creator Marketing Solutions <br /> for <span className="text-[#F482C0]">Every Industry</span>
                        </h1>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed font-light text-xl">
                            Oye Creators helps brands across categories grow visibility, trust, and conversions using verified hyperlocal creators. Whether you’re a local business or a national brand, we help you win where it matters most — on the ground.
                        </p>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <Link to="/contact" className="inline-block px-8 py-4 bg-primary-500 text-white rounded-full font-bold hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/30 text-lg">
                                Explore Solutions <span className="ml-2">&gt;</span>
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* TRUST / SOCIAL PROOF */}
            <section className="py-12 border-b border-gray-100 bg-white">
                <div className="container mx-auto px-6 max-w-7xl">
                    <p className="text-center text-sm font-bold text-gray-400 uppercase tracking-wider mb-8">Used by brands, organizations, and leaders across categories</p>
                    <motion.div
                        className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-60 grayscale hover:grayscale-0 transition-all duration-500"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 0.6 }}
                        transition={{ duration: 1 }}
                    >
                        <span className="text-xl font-bold text-gray-800">Local Brands</span>
                        <span className="text-xl font-bold text-gray-800">Enterprises</span>
                        <span className="text-xl font-bold text-gray-800">Agencies</span>
                        <span className="text-xl font-bold text-gray-800">Public Campaigns</span>
                    </motion.div>
                </div>
            </section>

            {/* WHAT MAKES OYE DIFFERENT */}
            <section className="py-24 bg-[#F9B4DA1A]">
                <div className="container mx-auto px-6 max-w-7xl">
                    <motion.h2
                        className="text-3xl md:text-4xl font-bold text-center mb-16"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        Why Brands Choose <span className="text-[#F482C0]">Oye Creators</span>
                    </motion.h2>

                    <motion.div
                        className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <motion.div variants={fadeInUp} className="p-8 rounded-3xl bg-white hover:shadow-xl transition-all border border-gray-50 group">
                            <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-500 mb-6 group-hover:scale-110 transition-transform">
                                <LocationOn fontSize="large" />
                            </div>
                            <h3 className="text-xl font-bold mb-3">Hyperlocal First</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                Reach audiences through creators who live, influence, and engage in the same locations.
                            </p>
                        </motion.div>

                        <motion.div variants={fadeInUp} className="p-8 rounded-3xl bg-white hover:shadow-xl transition-all border border-gray-50 group">
                            <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center text-green-500 mb-6 group-hover:scale-110 transition-transform">
                                <Handshake fontSize="large" />
                            </div>
                            <h3 className="text-xl font-bold mb-3">Authentic Influence</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                Real people. Real voices. Content that feels native, not forced.
                            </p>
                        </motion.div>

                        <motion.div variants={fadeInUp} className="p-8 rounded-3xl bg-white hover:shadow-xl transition-all border border-gray-50 group">
                            <div className="w-14 h-14 bg-purple-50 rounded-2xl flex items-center justify-center text-purple-500 mb-6 group-hover:scale-110 transition-transform">
                                <RocketLaunch fontSize="large" />
                            </div>
                            <h3 className="text-xl font-bold mb-3">Scalable Campaigns</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                Run campaigns with 10 or 1,000 creators without operational complexity.
                            </p>
                        </motion.div>

                        <motion.div variants={fadeInUp} className="p-8 rounded-3xl bg-white hover:shadow-xl transition-all border border-gray-50 group">
                            <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-500 mb-6 group-hover:scale-110 transition-transform">
                                <AdsClick fontSize="large" />
                            </div>
                            <h3 className="text-xl font-bold mb-3">Outcome Driven</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                Designed to drive awareness, engagement, leads, footfall, or action — not just views.
                            </p>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* SOLUTIONS BY CATEGORY */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-6 max-w-7xl">
                    <motion.div
                        className="text-center max-w-3xl mx-auto mb-16"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">Solutions by Category</h2>
                        <p className="text-lg text-gray-600">
                            Oye Creators works across multiple industries, adapting creator strategies to match each category’s audience and goals.
                        </p>
                    </motion.div>

                    <motion.div
                        className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {[
                            { title: 'Apparel & Fashion', desc: 'Drive trust, trials, and sales with hyperlocal creators.', link: '/solutions/fashion', icon: <ShoppingBag fontSize="large" />, color: 'text-pink-500', bg: 'bg-pink-50' },
                            { title: 'Real Estate', desc: 'Drive local trust, site visits, and qualified inquiries.', icon: <Apartment fontSize="large" />, color: 'text-blue-500', bg: 'bg-blue-50' },
                            { title: 'Retail & D2C', desc: 'Boost store footfall and product discovery locally.', icon: <ShoppingBag fontSize="large" />, color: 'text-orange-500', bg: 'bg-orange-50' },
                            { title: 'Hospitality & Food', desc: 'Create buzz, reviews, and repeat visits.', icon: <Restaurant fontSize="large" />, color: 'text-red-500', bg: 'bg-red-50' },
                            { title: 'Politics & Campaigns', desc: 'Build credibility and reach voters at a grassroots level.', icon: <Campaign fontSize="large" />, color: 'text-indigo-500', bg: 'bg-indigo-50' },
                            { title: 'Education', desc: 'Engage students and parents through trusted voices.', icon: <School fontSize="large" />, color: 'text-green-500', bg: 'bg-green-50' },
                            { title: 'Finance & Insurance', desc: 'Simplify complex offerings with relatable storytelling.', icon: <AccountBalance fontSize="large" />, color: 'text-cyan-500', bg: 'bg-cyan-50' },
                            { title: 'Healthcare & Wellness', desc: 'Build trust and awareness responsibly.', icon: <MedicalServices fontSize="large" />, color: 'text-teal-500', bg: 'bg-teal-50' },
                            { title: 'Government & CSR', desc: 'Communicate impact and initiatives at scale.', icon: <Gavel fontSize="large" />, color: 'text-gray-600', bg: 'bg-gray-100' },
                        ].map((item, idx) => (
                            <motion.div variants={fadeInUp} key={idx} className="h-full">
                                <Link to={item.link} className="group no-underline hover:no-underline bg-gray-50 p-8 rounded-3xl transition-all duration-300 hover:bg-white hover:shadow-xl border border-transparent hover:border-gray-100 block h-full">
                                    <div className={`mb-6 p-4 rounded-2xl w-fit ${item.bg} ${item.color} group-hover:scale-110 transition-all duration-300`}>
                                        {item.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">{item.title}</h3>
                                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                                </Link>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="py-24 bg-[#AC8EE11A] overflow-hidden">
                <div className="container mx-auto px-6 max-w-7xl">
                    <motion.h2
                        className="text-3xl md:text-4xl font-bold text-center mb-20"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        How It Works
                    </motion.h2>

                    <motion.div
                        className="grid md:grid-cols-5 gap-8 relative"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {/* Connecting Line (Desktop) */}
                        <div className="hidden md:block absolute top-[2.5rem] left-0 w-full h-1 bg-white/50 -z-10"></div>

                        {[
                            { step: '01', title: 'Define Objective', desc: 'Awareness, engagement, leads, or action.' },
                            { step: '02', title: 'Select Creators', desc: 'Hyperlocal, niche, and audience-matched.' },
                            { step: '03', title: 'Create Content', desc: 'Platform-native and location-first.' },
                            { step: '04', title: 'Launch & Amplify', desc: 'Across social platforms and regions.' },
                            { step: '05', title: 'Measure Impact', desc: 'Transparent reporting and insights.' },
                        ].map((item, idx) => (
                            <motion.div variants={fadeInUp} key={idx} className="relative text-center group">
                                <div className="w-20 h-20 mx-auto bg-white border-4 border-[#AC8EE11A] rounded-full flex items-center justify-center text-xl font-black text-gray-300 mb-6 group-hover:border-primary-500 group-hover:text-primary-500 transition-colors shadow-sm z-10">
                                    {item.step}
                                </div>
                                <h4 className="text-lg font-bold mb-2">{item.title}</h4>
                                <p className="text-sm text-gray-600">{item.desc}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-primary-500 text-white py-24">
                <div className="container mx-auto px-6 max-w-7xl text-center">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        <h2 className="text-3xl md:text-5xl font-extrabold mb-6 text-white">One Platform. Unlimited Categories.</h2>
                        <p className="text-xl text-white/90 max-w-2xl mx-auto mb-12">
                            No matter your industry, Oye Creators helps you connect with audiences through voices they already trust.
                        </p>
                        <motion.div whileHover={{ scale: 1.05 }}>
                            <Link to="/contact" className="inline-block px-10 py-5 bg-white text-primary-600 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors shadow-xl">
                                Talk to Oye Creators
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default IndustriesPage;
